import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { IsoTimestamp } from '@nav/shared';

/**
 * The header shows `Data per <timestamp>`, but the timestamp belongs to the
 * currently selected fund (`FundSummary.dataFreshness`) — which only the page
 * knows. Rather than lifting all of the funds page's state into the shell, the
 * page publishes its freshness here and the shell subscribes.
 */

interface FreshnessValue {
  freshness: IsoTimestamp | null;
  setFreshness: (value: IsoTimestamp | null) => void;
}

const DataFreshnessContext = createContext<FreshnessValue>({
  freshness: null,
  setFreshness: () => {},
});

export function DataFreshnessProvider({ children }: { children: ReactNode }) {
  const [freshness, setFreshness] = useState<IsoTimestamp | null>(null);
  const value = useMemo(() => ({ freshness, setFreshness }), [freshness]);
  return <DataFreshnessContext.Provider value={value}>{children}</DataFreshnessContext.Provider>;
}

export function useDataFreshness(): IsoTimestamp | null {
  return useContext(DataFreshnessContext).freshness;
}

/** Publish the active fund's freshness stamp for as long as this page is mounted. */
export function usePublishDataFreshness(value: IsoTimestamp | null | undefined): void {
  const { setFreshness } = useContext(DataFreshnessContext);
  useEffect(() => {
    setFreshness(value ?? null);
    return () => setFreshness(null);
  }, [value, setFreshness]);
}
